import React, { useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Heading,
  Grid,
  GridItem,
  VStack,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  useColorModeValue,
  Avatar,
  Tag,
  TagLabel,
  TagLeftIcon,
  Tooltip,
  Divider,
} from '@chakra-ui/react';
import { 
  Search, 
  Filter, 
  ChevronDown, 
  MoreVertical, 
  Lightbulb, 
  ThumbsUp,
  MessageSquare,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Tag as TagIcon,
  User,
  Calendar,
  BarChart,
  Brain
} from 'lucide-react';

const IdeaManagementPanel: React.FC = () => {
  // Colors
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const methodistBlue = '#0066CC';
  
  // State for filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  
  // Sample idea data
  const ideas = [
    {
      id: 'IDEA-1023',
      title: 'AI-guided meditation sessions',
      description: 'Implement personalized meditation sessions guided by AI that adapt to the clinician\'s stress levels and preferences.',
      author: 'Dr. Sarah Chen',
      department: 'Psychiatry',
      dateSubmitted: '2025-02-28',
      status: 'Under Review',
      votes: 24,
      comments: 8,
      tags: ['Wellness', 'AI Integration', 'Stress Management'],
      priority: 'High',
      llmInsights: 'This idea aligns with our stress reduction initiative and could leverage existing meditation content. Implementation complexity is moderate.',
      similarIdeas: 2
    },
    {
      id: 'IDEA-1022',
      title: 'Wellness dashboard customization',
      description: 'Allow clinicians to customize their wellness dashboard to show the metrics and activities most relevant to their personal wellness journey.',
      author: 'Dr. James Wilson',
      department: 'Internal Medicine',
      dateSubmitted: '2025-02-27',
      status: 'Approved',
      votes: 18,
      comments: 5,
      tags: ['UI/UX', 'Personalization', 'Dashboard'],
      priority: 'Medium',
      llmInsights: 'Customization would increase engagement. Technical complexity is low as we already have component-based dashboard architecture.',
      similarIdeas: 1
    },
    {
      id: 'IDEA-1021',
      title: 'One-click stress assessment',
      description: 'Create a quick, one-click stress assessment tool that clinicians can use multiple times throughout their day to track stress levels.',
      author: 'Dr. Maria Rodriguez',
      department: 'Emergency Medicine',
      dateSubmitted: '2025-02-26',
      status: 'Implemented',
      votes: 42,
      comments: 12,
      tags: ['Stress Management', 'Quick Tools', 'Data Collection'],
      priority: 'High',
      llmInsights: 'This would provide valuable longitudinal data for our stress prediction models while being minimally intrusive for users.',
      similarIdeas: 0
    },
    {
      id: 'IDEA-1020',
      title: 'Peer support matching algorithm',
      description: 'Develop an algorithm that matches clinicians for peer support based on specialty, experience, challenges, and personality compatibility.',
      author: 'Dr. Robert Johnson',
      department: 'Administration',
      dateSubmitted: '2025-02-25',
      status: 'Needs Clarification',
      votes: 15,
      comments: 7,
      tags: ['Community', 'Peer Support', 'Matching Algorithm'],
      priority: 'Medium',
      llmInsights: 'This would strengthen our Culture of Wellness pillar. Need more details on matching criteria and privacy considerations.',
      similarIdeas: 3
    },
    {
      id: 'IDEA-1019',
      title: 'Integration with hospital scheduling system',
      description: 'Connect JIMI with the hospital scheduling system to automatically suggest wellness activities during breaks and downtime.',
      author: 'Dr. Emily Parker',
      department: 'Surgery',
      dateSubmitted: '2025-02-24',
      status: 'Rejected',
      votes: 32,
      comments: 9,
      tags: ['Integration', 'Scheduling', 'Workflow'],
      priority: 'Low',
      llmInsights: 'Technical barriers with existing hospital systems make this challenging. Consider alternative approaches to achieve similar goals.',
      similarIdeas: 1
    }
  ];
  
  // Get status badge color
  const getStatusColor = (status: string) => {
    switch(status) {
      case 'Implemented': return 'green';
      case 'Approved': return 'orange';
      case 'Under Review': return 'blue';
      case 'Needs Clarification': return 'purple';
      case 'Rejected': return 'red';
      default: return 'gray';
    }
  };
  
  // Get priority badge color
  const getPriorityColor = (priority: string) => {
    switch(priority) {
      case 'High': return 'red';
      case 'Medium': return 'orange';
      case 'Low': return 'green';
      default: return 'gray';
    }
  };
  
  return (
    <VStack spacing={4} align="stretch">
      {/* Filters and search */}
      <Flex direction={{ base: 'column', md: 'row' }} gap={3} wrap="wrap">
        <InputGroup maxW={{ base: '100%', md: '300px' }}>
          <InputLeftElement pointerEvents="none">
            <Search size={18} color="gray.400" />
          </InputLeftElement>
          <Input placeholder="Search ideas..." />
        </InputGroup>
        
        <HStack spacing={3} flex="1" justify="flex-end">
          <Select 
            size="md" 
            maxW="150px" 
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            defaultValue="all"
          >
            <option value="all">All Statuses</option>
            <option value="implemented">Implemented</option>
            <option value="approved">Approved</option>
            <option value="review">Under Review</option>
            <option value="clarification">Needs Clarification</option>
            <option value="rejected">Rejected</option>
          </Select>
          
          <Select 
            size="md" 
            maxW="170px"
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            defaultValue="all"
          >
            <option value="all">All Departments</option>
            <option value="psychiatry">Psychiatry</option>
            <option value="internal">Internal Medicine</option>
            <option value="emergency">Emergency Medicine</option>
            <option value="surgery">Surgery</option>
            <option value="admin">Administration</option>
          </Select>
          
          <Select 
            size="md" 
            maxW="150px"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            defaultValue="newest"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="votes">Most Votes</option>
            <option value="comments">Most Comments</option>
            <option value="priority">Priority</option>
          </Select>
          
          <Button leftIcon={<Filter size={16} />} colorScheme="blue" variant="outline">
            More Filters
          </Button>
        </HStack>
      </Flex>
      
      {/* Ideas table */}
      <Box 
        bg={bgColor} 
        borderRadius="md" 
        boxShadow="sm"
        borderWidth="1px"
        borderColor={borderColor}
        overflow="hidden"
      >
        <Table variant="simple" size="md">
          <Thead bg={useColorModeValue('gray.50', 'gray.700')}>
            <Tr>
              <Th>Idea</Th>
              <Th>Author</Th>
              <Th>Status</Th>
              <Th isNumeric>Votes</Th>
              <Th isNumeric>Comments</Th>
              <Th>Priority</Th>
              <Th>LLM Insights</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {ideas.map((idea) => (
              <Tr key={idea.id} _hover={{ bg: useColorModeValue('gray.50', 'gray.700') }}>
                <Td>
                  <VStack align="start" spacing={1}>
                    <HStack>
                      <Text fontWeight="medium">{idea.title}</Text>
                      {idea.similarIdeas > 0 && (
                        <Tooltip label={`${idea.similarIdeas} similar ideas found`}>
                          <Badge colorScheme="purple" fontSize="xs">+{idea.similarIdeas}</Badge>
                        </Tooltip>
                      )}
                    </HStack>
                    <Text fontSize="xs" color="gray.500" noOfLines={2}>{idea.description}</Text>
                    <HStack spacing={1} mt={1}>
                      {idea.tags.map((tag, idx) => (
                        <Tag size="sm" key={idx} colorScheme="blue" variant="subtle">
                          <TagLabel fontSize="xs">{tag}</TagLabel>
                        </Tag>
                      ))}
                    </HStack>
                  </VStack>
                </Td>
                <Td>
                  <HStack>
                    <Avatar size="xs" name={idea.author} />
                    <VStack spacing={0} align="start">
                      <Text fontSize="sm">{idea.author}</Text>
                      <Text fontSize="xs" color="gray.500">{idea.department}</Text>
                    </VStack>
                  </HStack>
                </Td>
                <Td>
                  <Badge colorScheme={getStatusColor(idea.status)}>{idea.status}</Badge>
                </Td>
                <Td isNumeric>
                  <HStack justify="flex-end">
                    <ThumbsUp size={14} />
                    <Text>{idea.votes}</Text>
                  </HStack>
                </Td>
                <Td isNumeric>
                  <HStack justify="flex-end">
                    <MessageSquare size={14} />
                    <Text>{idea.comments}</Text>
                  </HStack>
                </Td>
                <Td>
                  <Badge colorScheme={getPriorityColor(idea.priority)}>{idea.priority}</Badge>
                </Td>
                <Td maxW="200px">
                  <HStack>
                    <Brain size={14} color="purple" />
                    <Text fontSize="xs" noOfLines={2}>{idea.llmInsights}</Text>
                  </HStack>
                </Td>
                <Td>
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      aria-label="Options"
                      icon={<MoreVertical size={16} />}
                      variant="ghost"
                      size="sm"
                    />
                    <MenuList>
                      <MenuItem icon={<MessageSquare size={14} />}>Add Comment</MenuItem>
                      <MenuItem icon={<CheckCircle size={14} />}>Change Status</MenuItem>
                      <MenuItem icon={<BarChart size={14} />}>View Analytics</MenuItem>
                      <MenuItem icon={<TagIcon size={14} />}>Edit Tags</MenuItem>
                    </MenuList>
                  </Menu>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      
      {/* Pagination and summary */}
      <Flex justify="space-between" align="center">
        <Text fontSize="sm" color="gray.500">Showing 5 of 243 ideas</Text>
        <HStack>
          <Button size="sm" variant="outline">Previous</Button>
          <Button size="sm" variant="solid" colorScheme="blue">1</Button>
          <Button size="sm" variant="outline">2</Button>
          <Button size="sm" variant="outline">3</Button>
          <Button size="sm" variant="outline">Next</Button>
        </HStack>
      </Flex>
    </VStack>
  );
};

export default IdeaManagementPanel;
